import { map } from "lodash";
import convert from "convert-units";

// A UM is "dimensional" when it names a length unit (FT, IN, M, ...). Bodies
// with a dimensional UM consume stock by length; anything else (EA, etc.) is a
// count of one piece per body.
const LENGTH_UNITS = convert().possibilities("length"); // mm, cm, m, in, ft, yd, mi, ...
export function isDimensional(um) {
  return Boolean(um) && LENGTH_UNITS.includes(String(um).toLowerCase());
}

// Plan the stock adjustments for linking/unlinking materials on a job item.
// Pure — no Firebase. Everything is validated and computed here BEFORE any
// write happens, so a bad length or unit aborts the whole update with nothing
// deducted (previously a failure partway through left earlier parts deducted,
// and every retry deducted them again).
//
// materialsById: { [materialId]: { exists: boolean, um: string } } for every
// material referenced by a changed link.
//
// Returns [{ materialId, delta }] where delta is signed: negative consumes
// stock, positive returns it. Throws a descriptive Error if any part cannot
// be planned.
//
// Each body carries its own `um` and, when that UM is dimensional, a `length`
// — both set on the item create/edit screen. Deduction rules per body:
//   dimensional um  -> convert length from body.um to the material's stock UM
//   non-dimensional -> one piece
//   empty um        -> legacy body (predates per-body UMs): one piece
// Connections are always a count of one. The item-level length/lengthUnits
// fields no longer exist on the form and never drive inventory; with multiple
// bodies a single item length deducted wrongly from every body's material.
export function planInventoryOps(localItem, currentItem, materialsById) {
  const ops = [];

  const planType = (type) => {
    const isBody = type === "bodies";
    map(localItem[type] || [], (part, partIndex) => {
      const materialId = part.materialId || "";
      const prevPart = (currentItem[type] || [])[partIndex];
      const prevMaterialId = (prevPart && prevPart.materialId) || "";
      if (materialId === prevMaterialId) return;

      const label = part.description || `${type} ${partIndex + 1}`;

      const amountInStockUnits = (stockUm) => {
        if (!isBody) return 1;
        const bodyUm = part.um || "";
        if (!bodyUm) return 1; // legacy body — count one piece
        if (!isDimensional(bodyUm)) {
          if (isDimensional(stockUm)) {
            throw new Error(
              `Stock for ${label} is tracked in "${stockUm}" but the body's UM is "${bodyUm}". Give the body a dimensional UM and length on the item edit screen.`
            );
          }
          return 1; // one piece
        }
        // Dimensional body: consume by length. Accept legacy `qty` written by
        // an earlier revision of this feature.
        const raw =
          part.length !== undefined && part.length !== ""
            ? part.length
            : part.qty;
        const len = Number(raw);
        if (raw === undefined || raw === "" || Number.isNaN(len) || len <= 0) {
          throw new Error(
            `Enter a length for ${label} on the item edit screen.`
          );
        }
        if (bodyUm === stockUm) return len;
        if (!isDimensional(stockUm)) {
          throw new Error(
            `${label} is measured in "${bodyUm}" but its material's stock is tracked in "${
              stockUm || "no UM"
            }". Fix the material's UM (or the body's) before updating.`
          );
        }
        try {
          return convert(len)
            .from(bodyUm.toLowerCase())
            .to(stockUm.toLowerCase());
        } catch (e) {
          throw new Error(
            `Cannot convert ${label} from "${bodyUm}" to stock units "${stockUm}". Fix the units before updating.`
          );
        }
      };

      if (materialId) {
        const mat = materialsById[materialId];
        if (!mat || !mat.exists) {
          throw new Error(
            `The material selected for ${label} no longer exists in inventory.`
          );
        }
        ops.push({ materialId, delta: -amountInStockUnits(mat.um) });
      }
      if (prevMaterialId) {
        const prev = materialsById[prevMaterialId];
        // A previously linked material that has since been deleted has no
        // stock to return to — skip rather than block the unlink.
        if (prev && prev.exists) {
          ops.push({
            materialId: prevMaterialId,
            delta: amountInStockUnits(prev.um),
          });
        }
      }
    });
  };

  planType("connections");
  planType("bodies");
  return ops;
}
